import React, { useState, useEffect, useRef } from 'react';
import styled from 'styled-components';

import DatePickerTab from './components/DatePickerTab';
import VacationOptionsPicker from '../VacationOptionsPicker';
import Calendar from '../Calendar';
import Footer from '../Footer';

import {
  DATE_PICKER_OPTION_NAME_TO_ICON,
  tabs,
  DATE_PICKER_OPTION_NAMES,
  type DatePickerTabsNames,
} from './constants';
import { DURATION_PICKER_MODE_NAMES } from '../VacationOptionsPicker/components/DurationPicker/constants';

import type { StartDatePreset, NightsPreset, Nights } from '@lt/api/dist/types/searchParams';
import type { StartDatePresetCategory } from '@lt/api/dist/methods/references/types';

import type { IDatePickerTabs, DatePickerOption } from './types';
import type { DurationPickerMode } from '../VacationOptionsPicker/components/DurationPicker/types';

import { convertVacationOptionsToDates } from '../../utils';

import CloseButton from '../../../CloseButton';
import { useOnClickOutside } from 'usehooks-ts';

const startDatePresetByName = (
  startDatePresets: StartDatePresetCategory[],
  name: string
): StartDatePreset =>
  startDatePresets
    .map((category) => category.options.flat().filter((preset) => preset.name === name))
    .filter((category) => category.length)[0][0];

export const nightsPresetByName = (
  nightsPresets: NightsPreset[],
  name?: string
): NightsPreset | undefined => {
  return nightsPresets.find((nightsPreset) => nightsPreset.name === name);
};

export const DatePickerTabs = (props: IDatePickerTabs): JSX.Element | null => {
  const {
    isMobile = false,

    // calendar
    open,
    leftValue,
    rightValue,
    nights,
    closeCalendar,
    onChangeCalendar,
    onChangeShift,
    currentShift,
    applyButtonHandler,
    minDate,
    excludeDates,
    isEndDateFocus,
    isWizard = false,
    isHotelOnly,
    searchCalendarDuration,
    indicationMap,
    setStartCurrentMonth,
    isFlexDatesOff,

    // Vacation options
    startDatePreset,
    nightsPreset,
    onChangeVacationOption,
    onButtonClick,
    vacationOptionsPresets,
  } = props;

  const ref = useRef<null | HTMLDivElement>(null);

  // хук который закрывает календарь (any потому что не смог нормально типизировать)
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  const handleClickOutside = isWizard ? () => null : (closeCalendar as (event: any) => void);
  useOnClickOutside(ref, handleClickOutside);

  const { startDatePresets, nightsPresets } = vacationOptionsPresets;

  const isPreset = !!(startDatePreset || nightsPreset || nights);

  const isVacationOptionsAvailable = startDatePresets.length > 0 && nightsPresets.length > 0;

  // Tabs logic
  const [selectedTab, selectTab] = useState<DatePickerOption>(
    isVacationOptionsAvailable && isPreset
      ? DATE_PICKER_OPTION_NAMES.VACATION_OPTIONS
      : DATE_PICKER_OPTION_NAMES.CONCRETE_DATES
  );

  const selectPresetTab = () => {
    selectTab(DATE_PICKER_OPTION_NAMES.VACATION_OPTIONS);
  };

  const handleTabsSelection = (selectedTab: DatePickerTabsNames) => {
    if (selectedTab === DATE_PICKER_OPTION_NAMES.VACATION_OPTIONS) {
      return selectPresetTab();
    }

    selectTab(selectedTab);
  };

  useEffect(() => {
    const newTab =
      isVacationOptionsAvailable && isPreset
        ? DATE_PICKER_OPTION_NAMES.VACATION_OPTIONS
        : DATE_PICKER_OPTION_NAMES.CONCRETE_DATES;
    handleTabsSelection(newTab);
  }, [isVacationOptionsAvailable]);

  const isVacationOptions =
    isVacationOptionsAvailable && selectedTab === DATE_PICKER_OPTION_NAMES.VACATION_OPTIONS;

  // Start date logic
  const selectStartDatePreset = (newStartDatePreset: string) => {
    onChangeVacationOption({
      startDatePreset: startDatePresetByName(startDatePresets, newStartDatePreset),
    });
  };

  // Duration logic
  const [selectedDurationPickerMode, setSelectedDurationPickerMode] = useState<DurationPickerMode>(
    DURATION_PICKER_MODE_NAMES.PRESET
  );

  const toggleDurationPickerMode = () => {
    if (isPresetNightsMode) {
      onButtonClick('concrete_nights');
      setSelectedDurationPickerMode(DURATION_PICKER_MODE_NAMES.EXACT);
    } else {
      setSelectedDurationPickerMode(DURATION_PICKER_MODE_NAMES.PRESET);
    }
  };

  const selectDuration = (newDuration: string | Nights) => {
    /* TODO: make obviously, separately set nights and preset, or add type, separate args naming */
    if (typeof newDuration === 'string') {
      onChangeVacationOption({
        nightsPreset: nightsPresetByName(nightsPresets, newDuration),
      });
    } else {
      onChangeVacationOption({ nights: newDuration });
    }
  };

  if (!open) return null;

  const isPresetNightsMode = selectedDurationPickerMode === DURATION_PICKER_MODE_NAMES.PRESET;

  // Не очень понял зачем и для чего, если кто знает опишите плиз
  const vacationOptionsDates = convertVacationOptionsToDates({
    startDateCell: leftValue,
    startDatePreset,
    nightsPreset,
    nights,
    shift: currentShift,
  });

  const startDate = isPreset ? vacationOptionsDates.startDate : leftValue.date;
  const endDate = isPreset ? vacationOptionsDates.endDate : rightValue.date;

  const allStartDatePresets = startDatePresets.reduce(
    (allPresets, category) => [...allPresets, ...category.options.flat()],
    []
  );
  const isCustomStartDatePreset = !allStartDatePresets.some(
    (preset) => preset.name === startDatePreset?.name
  );

  return (
    <>
      <StyledContainer $isMobile={isMobile} $isWizard={isWizard} ref={ref}>
        {isMobile && !isWizard && (
          <Header>
            <StyledCloseButton onClick={closeCalendar} />
          </Header>
        )}
        {isVacationOptionsAvailable ? (
          <TabsContainer>
            {tabs.map((tabProps) => (
              <DatePickerTab
                {...tabProps}
                key={tabProps.id}
                icon={DATE_PICKER_OPTION_NAME_TO_ICON[tabProps.id]}
                handleClick={() => {
                  if (tabProps.id === DATE_PICKER_OPTION_NAMES.VACATION_OPTIONS) {
                    onButtonClick('options_tab');
                  }

                  handleTabsSelection(tabProps.id);
                }}
                isSelected={tabProps.id === selectedTab}
              />
            ))}
          </TabsContainer>
        ) : null}
        {isVacationOptions ? (
          <VacationOptionsPicker
            isMobile={isMobile}
            isHotelOnly={isHotelOnly}
            selectedStartDatePreset={startDatePreset?.name}
            customStartDatePreset={isCustomStartDatePreset ? startDatePreset : undefined}
            selectStartDatePreset={selectStartDatePreset}
            selectedDurationPickerMode={selectedDurationPickerMode}
            toggleDurationPickerMode={toggleDurationPickerMode}
            vacationOptionsPresets={vacationOptionsPresets}
            nights={nights}
            nightsPreset={nightsPreset}
            selectDuration={selectDuration}
            startDateCell={leftValue}
            endDateCell={rightValue}
            handleNightsButtonClick={() => {
              onButtonClick('change_nights');
            }}
          />
        ) : (
          <Calendar
            open={open}
            closeCalendar={closeCalendar}
            startDate={startDate}
            endDate={endDate}
            onChange={onChangeCalendar}
            excludeDates={excludeDates}
            searchCalendarDuration={searchCalendarDuration}
            minDate={minDate}
            isMobile={isMobile}
            isWizard={isWizard}
            isEndDateFocus={isEndDateFocus}
            indicationMap={indicationMap}
            setStartCurrentMonth={setStartCurrentMonth}
            isPreset={isPreset}
            onClose={closeCalendar}
          />
        )}
        <Footer
          isFlexDatesOff={isFlexDatesOff}
          isMobile={isMobile}
          applyButtonHandler={applyButtonHandler}
          leftValue={leftValue}
          rightValue={rightValue}
          startDatePreset={startDatePreset}
          nights={nights}
          nightsPreset={nightsPreset}
          isHotelOnly={isHotelOnly}
          currentShift={currentShift}
          onChangeShift={onChangeShift}
          isVacationOptionsAvailable={isVacationOptionsAvailable}
          selectPresetTab={() => {
            onButtonClick('options_button');
            selectPresetTab();
          }}
        />
      </StyledContainer>
    </>
  );
};

const TabsContainer = styled.ul`
  display: flex;
`;

const StyledContainer = styled.div<{
  $isMobile: boolean;
  $isWizard?: boolean;
}>`
  display: flex;
  flex-direction: column;
  align-items: center;

  ${({ $isMobile }) => $isMobile && 'width: 100%;'}

  padding: ${({ $isMobile, $isWizard }) =>
    $isMobile && !$isWizard ? '72px 0 64px' : '16px 0 64px'};
  font-family: 'Backpack', Helvetica, sans-serif;
  font-size: 14px;
  line-height: 16px;

  border: none;
  display: flex;
  flex-direction: column;
  width: 100%;
  align-items: center;
  background-color: #fff;

  ${({ $isMobile, $isWizard }) =>
    $isMobile &&
    !$isWizard &&
    `
      top: 60px;
      position: fixed;
      left: 0;
      z-index: 120;
      height: 100vh;
    `}

  ${({ $isWizard }) =>
    $isWizard &&
    `
      height: 100vh;
    `}

  // desktop position
  ${({ $isMobile }) =>
    !$isMobile &&
    `
      position: absolute;
      width: auto;
      margin-top: 56px;
      border-radius: 4px;
      left: 50%;
      transform: translate(-50%, 0);

      z-index: 999;
      padding-bottom: 16px;
      box-shadow: 0 2px 4px rgb(210 208 197 / 80%);
    `};

  ${({ $isMobile, $isWizard }) =>
    $isMobile &&
    `
      ${!$isWizard && `width: 100%;`}
      display: flex;
      justify-content: center;
      overflow-y: scroll;
  `}

  z-index: 10;
`;

// @todo
// eslint-disable-next-line @typescript-eslint/ban-ts-comment
// @ts-ignore
const StyledCloseButton = styled(CloseButton).attrs({ $inMobileInput: true })`
  position: fixed;
  top: 20px;
  margin: 0 12px 0 0;
`;

const Header = styled.div`
  position: fixed;
  height: 64px;
  background: #f5f4f2;
  width: 100%;
  margin-bottom: 16px;
  z-index: 120;
  top: 0;

  &:after {
    content: '';
    position: fixed;
    top: 56px;
    height: 8px;
    width: 100%;
    background: #fff;
    border-radius: 8px 8px 0 0;
  }
`;
